import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef, useState } from 'react';
export function ActionsMenu({ label, items, onChoose }) {
    const menuId = useId();
    const [open, setOpen] = useState(false);
    /** The submenu that is unfolded, by the id of its item. */
    const [submenu, setSubmenu] = useState(null);
    const button = useRef(null);
    const root = useRef(null);
    const refs = useRef(new Map());
    /** Item to focus after the next render (the menu or the submenu has just appeared). */
    const pending = useRef(null);
    useEffect(() => {
        if (pending.current) {
            refs.current.get(pending.current)?.focus();
            pending.current = null;
        }
    });
    // A click outside closes it, without taking the focus back.
    useEffect(() => {
        if (!open)
            return undefined;
        const onPointerDown = (event) => {
            if (!root.current?.contains(event.target)) {
                setOpen(false);
                setSubmenu(null);
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [open]);
    const show = (focus) => {
        setOpen(true);
        setSubmenu(null);
        pending.current = (focus === 'first' ? items[0] : items[items.length - 1])?.id ?? null;
    };
    const close = () => {
        setOpen(false);
        setSubmenu(null);
        button.current?.focus();
    };
    const choose = (id) => {
        close();
        onChoose(id);
    };
    const unfold = (item) => {
        setSubmenu(item.id);
        pending.current = item.items?.[0]?.id ?? null;
    };
    /** Arrows, Home and End within one level of the menu. */
    const moveWithin = (event, level) => {
        const index = level.findIndex((item) => refs.current.get(item.id) === document.activeElement);
        const next = event.key === 'ArrowDown'
            ? (index + 1) % level.length
            : event.key === 'ArrowUp'
                ? (index - 1 + level.length) % level.length
                : event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                        ? level.length - 1
                        : null;
        if (next === null)
            return false;
        event.preventDefault();
        refs.current.get(level[next].id)?.focus();
        return true;
    };
    const onMenuKeyDown = (event) => {
        if (event.key === 'Escape') {
            // The panel closes on Esc too: this Esc is the menu's own.
            event.preventDefault();
            event.stopPropagation();
            close();
        }
        else if (event.key === 'Tab') {
            setOpen(false);
            setSubmenu(null);
        }
        else {
            moveWithin(event, items);
        }
    };
    const onSubmenuKeyDown = (event, parent) => {
        if (event.key === 'Escape' || event.key === 'ArrowLeft') {
            event.preventDefault();
            event.stopPropagation();
            setSubmenu(null);
            refs.current.get(parent.id)?.focus();
        }
        else if (moveWithin(event, parent.items ?? [])) {
            event.stopPropagation();
        }
    };
    const itemButton = (item) => (_jsx("button", { ref: (element) => {
            if (element)
                refs.current.set(item.id, element);
            else
                refs.current.delete(item.id);
        }, type: "button", role: "menuitem", tabIndex: -1, className: "actions-menu__item", ...(item.items
            ? { 'aria-haspopup': 'menu', 'aria-expanded': submenu === item.id }
            : {}), onClick: () => {
            if (!item.items)
                choose(item.id);
            else if (submenu === item.id)
                setSubmenu(null);
            else
                unfold(item);
        }, onKeyDown: (event) => {
            if (item.items &&
                (event.key === 'ArrowRight' || event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                event.stopPropagation();
                unfold(item);
            }
        }, children: item.label }));
    return (_jsxs("div", { className: "actions-menu", ref: root, children: [_jsx("button", { ref: button, type: "button", className: "actions-menu__button", "aria-label": label, "aria-haspopup": "menu", "aria-expanded": open, "aria-controls": open ? menuId : undefined, onClick: () => (open ? close() : show('first')), onKeyDown: (event) => {
                    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault();
                        show(event.key === 'ArrowDown' ? 'first' : 'last');
                    }
                }, children: "\u22EF" }), open ? (_jsx("ul", { id: menuId, role: "menu", "aria-label": label, className: "actions-menu__list", onKeyDown: onMenuKeyDown, children: items.map((item) => (_jsxs("li", { role: "none", children: [itemButton(item), item.items && submenu === item.id ? (_jsx("ul", { role: "menu", "aria-label": item.label, className: "actions-menu__list actions-menu__list--sub", onKeyDown: (event) => onSubmenuKeyDown(event, item), children: item.items.map((child) => (_jsx("li", { role: "none", children: itemButton(child) }, child.id))) })) : null] }, item.id))) })) : null] }));
}
//# sourceMappingURL=ActionsMenu.js.map